<template>
  <div class="topbar">
    <v-btn icon class="topbar__menu" aria-label="Meniu" @click="$emit('drawerEvent')">
      <v-icon>mdi-menu</v-icon>
    </v-btn>
    <span class="topbar__date">{{ today }}</span>
    <v-spacer />
    <ErrorAlert :message="errorMessage" @clear-error="clearMessage" />
    <SuccessAlert :message="successMessage" @clear-error="clearMessage" />

    <v-menu offset-y left nudge-bottom="8" min-width="300" max-width="360">
      <template v-slot:activator="{ attrs, on }">
        <button type="button" class="topbar__user" v-bind="attrs" v-on="on">
          <span class="topbar__avatar" aria-hidden="true">{{ initials }}</span>
          <span class="topbar__who">
            <span class="topbar__name">{{ fullName }}</span>
            <span class="topbar__role">{{ roleText }}</span>
          </span>
          <v-icon class="topbar__chevron">mdi-chevron-down</v-icon>
        </button>
      </template>

      <div class="usermenu">
        <div class="usermenu__head">
          <span class="topbar__avatar topbar__avatar--lg" aria-hidden="true">{{ initials }}</span>
          <div>
            <div class="usermenu__name">{{ fullName }}</div>
            <div class="usermenu__role">{{ roleText }}</div>
          </div>
        </div>
        <div v-if="departments.length" class="usermenu__depts">
          <div class="usermenu__label">{{ departments.length > 1 ? "Departamentele dvs." : "Departamentul dvs." }}</div>
          <div v-for="d in departments" :key="d.name" class="usermenu__dept">
            <span>{{ d.name }}</span>
            <span v-if="d.isSef" class="usermenu__sef">Șef</span>
          </div>
        </div>
        <v-divider />
        <v-list dense class="py-1">
          <v-list-item :to="{ name: 'profile' }">
            <v-list-item-icon><v-icon>mdi-account-cog-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Contul meu</v-list-item-title>
          </v-list-item>
          <v-list-item @click.stop>
            <v-list-item-icon><v-icon>mdi-bell-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Notificări Windows</v-list-item-title>
            <v-list-item-action class="my-0 usermenu__switch">
              <v-switch v-model="notificationsEnabled" inset dense hide-details color="primary" @change="toggleNotifications" />
            </v-list-item-action>
          </v-list-item>
          <v-list-item @click.stop>
            <v-list-item-icon><v-icon>mdi-format-line-spacing</v-icon></v-list-item-icon>
            <v-list-item-title>Afișare compactă</v-list-item-title>
            <v-list-item-action class="my-0 usermenu__switch">
              <v-switch v-model="compact" inset dense hide-details color="primary" aria-label="Afișare compactă" />
            </v-list-item-action>
          </v-list-item>
          <v-divider class="my-1" />
          <v-list-item @click="logout">
            <v-list-item-icon><v-icon>mdi-logout</v-icon></v-list-item-icon>
            <v-list-item-title>Ieșire</v-list-item-title>
          </v-list-item>
        </v-list>
      </div>
    </v-menu>
  </div>
</template>

<script>
import * as helper from "@/helper/helper";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import { roleLabel } from "@/config/roles";
import { display, setCompact } from "@/helper/display";

export default {
  name: "TopBar",
  components: { SuccessAlert, ErrorAlert },
  data() {
    return {
      notificationsEnabled: typeof Notification !== "undefined" && Notification.permission === "granted",
      errorMessage: "",
      successMessage: "",
    };
  },
  computed: {
    user() {
      return this.$store.getters.getUser || {};
    },
    fullName() {
      return [this.user.firstName, this.user.lastName].filter(Boolean).join(" ");
    },
    initials() {
      return ((this.user.firstName || " ")[0] + (this.user.lastName || " ")[0]).toUpperCase().trim();
    },
    roleText() {
      return roleLabel(this.user.role);
    },
    departments() {
      return this.$store.getters.myDepartments;
    },
    compact: {
      get() {
        return display.compact;
      },
      set(on) {
        setCompact(on);
      },
    },
    today() {
      const text = new Date().toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
  },
  methods: {
    async toggleNotifications() {
      if (Notification.permission === "denied") {
        this.notificationsEnabled = false;
        this.handleError("Notificările sunt blocate. Le puteți debloca din setările browserului (chrome://settings/content).");
        return;
      }
      if (Notification.permission === "granted") {
        this.notificationsEnabled = true;
        this.handleSuccess("Notificările sunt deja activate. Le puteți opri din setările browserului.");
        return;
      }
      await helper.requestNotificationPermission();
      this.notificationsEnabled = Notification.permission === "granted";
      if (this.notificationsEnabled) this.handleSuccess("Notificările au fost activate");
      else this.handleError("Notificările vor fi blocate. Le puteți debloca din setările browserului.");
    },
    logout() {
      this.$store.dispatch("logout");
      this.$emit("logout");
    },
    handleError(message) {
      this.errorMessage = message;
    },
    handleSuccess(message) {
      this.successMessage = message;
    },
    clearMessage() {
      this.errorMessage = "";
      this.successMessage = "";
    },
  },
};
</script>

<style scoped>
.topbar {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 68px;
  padding: 10px var(--gutter) 6px;
  background: var(--pine-900);
  color: #fff;
}
.topbar__menu.v-btn {
  color: rgba(255, 255, 255, 0.85) !important;
  margin-left: -8px;
}
.topbar__date {
  font-size: 0.95rem;
  color: rgba(195, 233, 211, 0.8);
}
.topbar__user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 8px 5px 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #fff;
  font: inherit;
  cursor: pointer;
  text-align: left;
}
.topbar__user:hover {
  background: rgba(255, 255, 255, 0.11);
}
.topbar__avatar {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--leaf);
  color: var(--pine-900);
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1rem;
  flex: none;
}
.topbar__avatar--lg {
  width: 48px;
  height: 48px;
  font-size: 1.15rem;
}
.topbar__who {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.topbar__name {
  font-weight: 700;
  font-size: 1rem;
}
.topbar__role {
  font-size: 0.82rem;
  color: rgba(195, 233, 211, 0.85);
}
.topbar__chevron.v-icon {
  color: rgba(255, 255, 255, 0.7);
}
.usermenu {
  background: #fff;
}
.usermenu__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 18px 14px;
}
.usermenu__name {
  font-weight: 700;
  font-size: 1.05rem;
}
.usermenu__role {
  color: var(--ink-3);
  font-size: 0.9rem;
}
.usermenu__depts {
  padding: 0 18px 14px;
}
.usermenu__label {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--ink-3);
  margin-bottom: 6px;
}
.usermenu__dept {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 0;
  font-size: 0.95rem;
  border-top: 1px solid var(--line-soft);
}
/* the inset track is wider than the switch box and touched the menu edge */
.usermenu__switch {
  margin-right: 10px;
}
.usermenu__sef {
  flex: none;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--pine-900);
  color: var(--leaf);
  font-size: 0.78rem;
  font-weight: 700;
}
html.density-compact .topbar {
  min-height: 54px;
  padding-top: 6px;
  padding-bottom: 4px;
}
html.density-compact .topbar__avatar:not(.topbar__avatar--lg) {
  width: 34px;
  height: 34px;
}
@media (max-width: 700px) {
  .topbar__date,
  .topbar__who {
    display: none;
  }
}
</style>
